import i18n from 'i18next';
import type { PlatformStatus } from '@/shared/services/platforms.service';
import { ERROR_MESSAGE_KEYS, errorCodeOf, getApiErrorMessage } from '@/shared/lib/api-errors';

/**
 * "Coming soon": a channel the account cannot connect yet (an admin's
 * `channel.*` switch, or the account's override). A channel that is already
 * connected is never coming soon — it keeps publishing and may be
 * reconnected. An API that does not send `available` yet means available.
 */
export function isChannelComingSoon(status: Pick<PlatformStatus, 'connected' | 'available'> | undefined): boolean {
  if (!status) return false;
  return !status.connected && status.available === false;
}

/** The statuses as a set of uppercase platform names that are coming soon. */
export function comingSoonPlatforms(statuses: readonly PlatformStatus[] | undefined): Set<string> {
  const out = new Set<string>();
  for (const s of statuses ?? []) {
    if (isChannelComingSoon(s)) out.add(s.platform.toUpperCase());
  }
  return out;
}

/** Brand names, never translated (the API's enum, or the app's PlatformId). */
const PLATFORM_BRAND_NAMES: Readonly<Record<string, string>> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TWITTER: 'X',
  LINKEDIN: 'LinkedIn',
  WHATSAPP: 'WhatsApp',
  TELEGRAM: 'Telegram',
  YOUTUBE: 'YouTube',
  PINTEREST: 'Pinterest',
  TIKTOK: 'TikTok',
  GOOGLE_BUSINESS: 'Google Business Profile',
  ZETPIN: 'Zetpin',
};

/** Whether `platform` names a channel the app knows (an API enum, any case). */
export function isKnownPlatform(platform: string): boolean {
  return PLATFORM_BRAND_NAMES[platform.toUpperCase()] !== undefined;
}

/** "GOOGLE_BUSINESS" or "google_business" → "Google Business Profile"; anything unknown as it came. */
export function platformBrandName(platform: string): string {
  return PLATFORM_BRAND_NAMES[platform.toUpperCase()] ?? platform;
}

/**
 * Codes the channel connect flows send (OAuth callback, Telegram and
 * WhatsApp forms, the pickers), with the app's own words for them. Read
 * before ERROR_MESSAGE_KEYS, so these are translated even before the shared
 * map lists them. `{{platform}}` in a text is the brand name.
 */
export const CHANNEL_ERROR_KEYS: Readonly<Record<string, string>> = {
  OAUTH_DENIED: 'errors.oauthDenied',
  OAUTH_CLIENT_INVALID: 'errors.oauthClientInvalid',
  OAUTH_SCOPE_MISSING: 'errors.oauthScopeMissing',
  OAUTH_PERMISSION_DECLINED: 'errors.oauthPermissionDeclined',
  OAUTH_METHOD_UNAVAILABLE: 'errors.oauthMethodUnavailable',
  LINKEDIN_NO_ORGANIZATION: 'errors.linkedinNoOrganization',
  LINKEDIN_ORGANIZATION_NOT_FOUND: 'errors.linkedinOrganizationNotFound',
  OAUTH_RATE_LIMITED: 'errors.oauthRateLimited',
  OAUTH_PROVIDER_DOWN: 'errors.oauthProviderDown',
  OAUTH_PROVIDER_ERROR: 'errors.oauthProviderError',
  OAUTH_STATE_EXPIRED: 'errors.oauthStateExpired',
  OAUTH_INCOMPLETE: 'errors.oauthIncomplete',
  OAUTH_EXCHANGE_FAILED: 'errors.oauthExchangeFailed',
  OAUTH_PLATFORM_MISMATCH: 'errors.oauthPlatformMismatch',
  OAUTH_BROWSER_MISMATCH: 'errors.oauthBrowserMismatch',
  OAUTH_ACCOUNT_UNREADABLE: 'errors.oauthAccountUnreadable',
  YOUTUBE_NO_CHANNEL: 'errors.youtubeNoChannel',
  GBP_ACCESS_DENIED: 'errors.gbpAccessDenied',
  GBP_LOCATIONS_UNAVAILABLE: 'errors.gbpLocationsUnavailable',
  GBP_NO_LOCATION: 'errors.gbpNoLocation',
  META_PAGES_UNAVAILABLE: 'errors.metaPagesUnavailable',
  META_NO_PAGE: 'errors.metaNoPage',
  META_NO_INSTAGRAM_PAGE: 'errors.metaNoInstagramPage',
  TELEGRAM_TOKEN_REJECTED: 'errors.telegramTokenRejected',
  TELEGRAM_CHAT_ID_REQUIRED: 'errors.telegramChatIdRequired',
  TELEGRAM_CHAT_NOT_FOUND: 'errors.telegramChatNotFound',
  TELEGRAM_BOT_NOT_IN_CHAT: 'errors.telegramBotNotInChat',
  TELEGRAM_WEBHOOK_REJECTED: 'errors.telegramWebhookRejected',
  TELEGRAM_UNREACHABLE: 'errors.telegramUnreachable',
  TELEGRAM_BOT_IN_USE: 'errors.telegramBotInUse',
  WHATSAPP_SIGNUP_NOT_CONFIGURED: 'channels.whatsapp.signupNotConfigured',
  WHATSAPP_SIGNUP_REJECTED: 'errors.whatsappSignupRejected',
  WHATSAPP_TOKEN_REJECTED: 'errors.whatsappTokenRejected',
  WHATSAPP_WABA_NOT_ACCESSIBLE: 'errors.whatsappWabaNotAccessible',
  WHATSAPP_PHONE_NUMBERS_UNREADABLE: 'errors.whatsappPhoneNumbersUnreadable',
  WHATSAPP_PHONE_NOT_FOUND: 'errors.whatsappPhoneNotFound',
  WHATSAPP_SUBSCRIBE_FAILED: 'errors.whatsappSubscribeFailed',
  WHATSAPP_REGISTER_FAILED: 'errors.whatsappRegisterFailed',
  PINTEREST_BOARD_NOT_FOUND: 'errors.pinterestBoardNotFound',
  ZETPIN_HANDLE_REQUIRED: 'errors.zetpinHandleRequired',
  ZETPIN_TOKEN_REJECTED: 'errors.zetpinTokenRejected',
  ZETPIN_ACCOUNT_NOT_FOUND: 'errors.zetpinAccountNotFound',
  ZETPIN_ACCOUNT_MISMATCH: 'errors.zetpinAccountMismatch',
  ZETPIN_UNREACHABLE: 'errors.zetpinUnreachable',
};

/** The permission features the app has words for (`channels.permission.<key>`). */
const PERMISSION_FEATURES: ReadonlySet<string> = new Set([
  'publish',
  'pages',
  'profile',
  'comments',
  'messages',
  'insights',
  'manageContent',
  'liveUpdates',
  'media',
  'boards',
  'businessProfile',
  'organizations',
]);

/**
 * "publish posts, see your Pages": the known features of a list (an array,
 * or the callback's comma-separated text), translated and joined in the
 * reader's language. Unknown keys (anything a URL could carry) are dropped.
 */
export function permissionList(missing: readonly string[] | string | null | undefined): string {
  const keys = (typeof missing === 'string' ? missing.split(',') : (missing ?? [])).filter((k) => PERMISSION_FEATURES.has(k));
  const names = [...new Set(keys)].map((k) => i18n.t(`channels.permission.${k}`));
  if (names.length === 0) return '';
  try {
    return new Intl.ListFormat(i18n.language || 'en', { style: 'long', type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
}

/** The translation for a channel error code, or undefined when the app has none. */
export function channelErrorText(
  code: string | null | undefined,
  platform?: string,
  missing?: readonly string[] | string | null,
): string | undefined {
  if (!code) return undefined;
  // A declined permission names what was switched off; without a list it is the generic line.
  if (code === 'OAUTH_PERMISSION_DECLINED') {
    const permissions = permissionList(missing);
    return i18n.t(permissions ? 'errors.oauthPermissionDeclined' : 'errors.oauthPermissionDeclinedGeneric', {
      platform: platform ? platformBrandName(platform) : '',
      permissions,
    });
  }
  const key = CHANNEL_ERROR_KEYS[code] ?? ERROR_MESSAGE_KEYS[code];
  if (!key || !i18n.exists(key)) return undefined;
  return i18n.t(key, { platform: platform ? platformBrandName(platform) : '' });
}

/**
 * The words for a failed OAuth callback: the app's own, translated, when the
 * callback page sent a code the app knows (OAUTH_DENIED, GBP_NO_LOCATION,
 * CHANNEL_UNAVAILABLE…), with the platform's brand name; otherwise the
 * page's English message.
 */
export function oauthCallbackError(payload: { error?: string; code?: string; platform?: string; missing?: string }): string | undefined {
  return channelErrorText(payload.code, payload.platform, payload.missing) ?? payload.error;
}

/**
 * A connect form's error (Telegram, WhatsApp, a picker): the channel code's
 * translation first, then what getApiErrorMessage makes of it.
 */
export function channelErrorMessage(err: unknown, fallback: string, platform?: string): string {
  return channelErrorText(errorCodeOf(err), platform) ?? getApiErrorMessage(err, fallback);
}
